import {
    collection,
    doc,
    getDocs,
    getFirestore,
    increment,
    onSnapshot,
    serverTimestamp,
    setDoc,
    runTransaction,
} from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { COLLECTIONS, STUDENT_BADGES_DEFAULT_BRANCH_ID } from '@constants/firestorePaths';

function db() {
    return getFirestore(getFirebaseApp());
}

const BRANCHES_SUB = 'branches';
const ITEMS_SUB = 'items';

/** Summary + counters for one campus (same branch ids as `schoolTime/{tenantId}/branches/{branchId}`). */
export function studentBadgesBranchDocRef(tenantId, branchId) {
    if (!tenantId || !branchId) throw new Error('Missing tenantId/branchId');
    return doc(db(), COLLECTIONS.STUDENT_BADGES, tenantId, BRANCHES_SUB, branchId);
}

export function studentBadgesItemsCollectionRef(tenantId, branchId) {
    if (!tenantId || !branchId) throw new Error('Missing tenantId/branchId');
    return collection(db(), COLLECTIONS.STUDENT_BADGES, tenantId, BRANCHES_SUB, branchId, ITEMS_SUB);
}

export function studentBadgeItemRef(tenantId, branchId, badgeId) {
    if (!tenantId || !branchId || !badgeId) throw new Error('Missing tenantId/branchId/badgeId');
    return doc(db(), COLLECTIONS.STUDENT_BADGES, tenantId, BRANCHES_SUB, branchId, ITEMS_SUB, badgeId);
}

/** @deprecated Pre–per-branch badges; used only to adjust counts for unmigrated docs. */
export function studentBadgeLegacyItemRef(tenantId, badgeId) {
    if (!tenantId || !badgeId) throw new Error('Missing tenantId/badgeId');
    return doc(db(), COLLECTIONS.STUDENT_BADGES, tenantId, ITEMS_SUB, badgeId);
}

function sortBadgeRows(rows) {
    return [...(rows || [])].sort((a, b) => {
        const y = (Number(b.year) || 0) - (Number(a.year) || 0);
        if (y !== 0) return y;
        return (Number(b.badgeNumber) || 0) - (Number(a.badgeNumber) || 0);
    });
}

export function subscribeStudentBadgeItems({ tenantId, branchId, onNext, onError }) {
    if (!tenantId || !branchId) throw new Error('Missing tenantId/branchId');
    const ref = studentBadgesItemsCollectionRef(tenantId, branchId);
    return onSnapshot(
        ref,
        (snap) => {
            const rows = snap.docs.map((d) => ({
                id: d.id,
                branchId,
                ...d.data(),
            }));
            onNext?.(sortBadgeRows(rows));
        },
        (err) => onError?.(err)
    );
}

export function subscribeStudentBadgeBranchSummary({ tenantId, branchId, onNext, onError }) {
    if (!tenantId || !branchId) throw new Error('Missing tenantId/branchId');
    const ref = studentBadgesBranchDocRef(tenantId, branchId);
    return onSnapshot(
        ref,
        (snap) => {
            onNext?.(snap.exists() ? { id: snap.id, branchId, ...snap.data() } : null);
        },
        (err) => onError?.(err)
    );
}

/**
 * Recomputes summary analytics from badge item docs for one campus.
 */
export async function refreshStudentBadgesSummary(tenantId, branchId) {
    if (!tenantId || !branchId) throw new Error('Missing tenantId/branchId');
    const snap = await getDocs(studentBadgesItemsCollectionRef(tenantId, branchId));
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const lastBadgeNumber = rows.reduce((m, r) => Math.max(m, Number(r.badgeNumber) || 0), 0);
    const badgesByYear = {};
    for (const r of rows) {
        const y = Number(r.year);
        if (!Number.isFinite(y)) continue;
        const key = String(y);
        badgesByYear[key] = (badgesByYear[key] || 0) + 1;
    }
    await setDoc(
        studentBadgesBranchDocRef(tenantId, branchId),
        {
            tenantId,
            branchId,
            totalBadges: rows.length,
            lastBadgeNumber,
            badgesByYear,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
}

/**
 * Creates a badge with auto-incrementing `badgeNumber` (1, 2, 3, … per campus), `title`, and `year`.
 */
export async function createStudentBadge({ tenantId, branchId, title, year }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!branchId) throw new Error('Branch is required');
    const cleanTitle = (title || '').trim();
    const y = Number(year);
    if (!cleanTitle) throw new Error('Title is required');
    if (!Number.isFinite(y) || y < 1900 || y > 2100) throw new Error('Invalid year');

    const itemsCol = studentBadgesItemsCollectionRef(tenantId, branchId);
    const summaryRef = studentBadgesBranchDocRef(tenantId, branchId);
    const itemRef = doc(itemsCol);

    await runTransaction(db(), async (tx) => {
        const sumSnap = await tx.get(summaryRef);
        const data = sumSnap.exists() ? sumSnap.data() : {};
        const last = Number(data.lastBadgeNumber) || 0;
        const total = Number(data.totalBadges) || 0;
        const nextNum = last + 1;

        tx.set(itemRef, {
            tenantId,
            branchId,
            title: cleanTitle,
            badgeNumber: nextNum,
            year: y,
            assignedStudentCount: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });

        tx.set(
            summaryRef,
            {
                tenantId,
                branchId,
                lastBadgeNumber: nextNum,
                totalBadges: total + 1,
                updatedAt: serverTimestamp(),
                ...(sumSnap.exists() ? {} : { createdAt: serverTimestamp() }),
            },
            { merge: true }
        );
    });

    await refreshStudentBadgesSummary(tenantId, branchId);
    return { id: itemRef.id, branchId };
}
